/**
 * Parse AI Calling prompts into a visual flow (Greeting Intro, Q1, Q2, …).
 */

import { resolvePromptForCall, TEST_CALL_DEMO_CANDIDATE_NAME } from '@/utils/ai-calling-prompt-defaults'

const Q_BLOCK_RE = /Q(\d+)\s*[–\-—]\s*([^:\n]+):\s*[\r\n\s]*"([^"]+)"/gm

/** Extract QUESTION FLOW section from system prompt. */
function extractQuestionFlowSection(systemPrompt) {
  const text = String(systemPrompt || '')
  const match = text.match(/QUESTION FLOW:\s*([\s\S]*?)(?:\n\s*(?:FAQ RESPONSES|SILENT CAPTURE|Closing after)|$)/i)
  if (match) return match[1]
  return text
}

/**
 * @param {{ firstMessage?: string, systemPrompt?: string, promptValues?: object }} opts
 * @returns {Array<{ id: string, label: string, templateText: string, spokenText: string, kind: string }>}
 */
export function parseScriptFlow(opts = {}) {
  const firstMessage = String(opts.firstMessage || '').trim()
  const systemPrompt = String(opts.systemPrompt || '')
  const promptValues = { candidate_name: TEST_CALL_DEMO_CANDIDATE_NAME, ...(opts.promptValues || {}) }
  const steps = []

  if (firstMessage) {
    steps.push({
      id: 'greeting',
      label: 'Greeting Intro',
      templateText: firstMessage,
      spokenText: resolvePromptForCall(firstMessage, promptValues),
      kind: 'greeting',
    })
  }

  const flowSection = extractQuestionFlowSection(systemPrompt)
  Q_BLOCK_RE.lastIndex = 0
  let match = Q_BLOCK_RE.exec(flowSection)
  while (match) {
    const num = match[1]
    const title = match[2].trim()
    const quote = match[3].trim()
    steps.push({
      id: `q${num}`,
      label: resolvePromptForCall(`Q${num}${title ? ` — ${title}` : ''}`, promptValues),
      templateText: quote,
      spokenText: resolvePromptForCall(quote, promptValues),
      kind: 'question',
    })
    match = Q_BLOCK_RE.exec(flowSection)
  }

  const closingMatch = flowSection.match(/Closing after Q\d+[^:]*:\s*\n?Say:\s*"([^"]+)"/i)
  if (closingMatch) {
    steps.push({
      id: 'closing',
      label: 'Closing',
      templateText: closingMatch[1],
      spokenText: resolvePromptForCall(closingMatch[1], promptValues),
      kind: 'closing',
    })
  }

  return steps
}

/**
 * Apply edited flow step texts back to first message and system prompt templates.
 * @param {Array<{ id: string, kind: string, templateText?: string, spokenText?: string }>} steps
 * @param {{ firstMessage?: string, systemPrompt?: string }} templates
 * @returns {{ firstMessage: string, systemPrompt: string }}
 */
export function syncFlowStepsToPrompts(steps, { firstMessage = '', systemPrompt = '' } = {}) {
  let newFirstMessage = String(firstMessage || '')
  let newSystemPrompt = String(systemPrompt || '')

  ;(steps || []).forEach(step => {
    const text = String(step.templateText ?? step.spokenText ?? '').trim()
    if (!text) return

    if (step.kind === 'greeting') {
      newFirstMessage = text
      return
    }

    if (step.kind === 'question') {
      const num = String(step.id || '').replace(/^q/i, '')
      if (!num) return
      const re = new RegExp(
        `(Q${num}\\s*[–\\-—][^:\\n]*:\\s*[\\r\\n\\s]*")([^"]*)(")`,
        'm',
      )
      newSystemPrompt = newSystemPrompt.replace(re, `$1${text}$3`)
      return
    }

    if (step.kind === 'closing') {
      newSystemPrompt = newSystemPrompt.replace(
        /(Closing after Q\d+[^:]*:\s*\n?Say:\s*")([^"]*)(")/i,
        `$1${text}$3`,
      )
    }
  })

  return {
    firstMessage: newFirstMessage,
    systemPrompt: newSystemPrompt,
  }
}

export default parseScriptFlow
